<template>
  <v-card>
    <div class="d-flex justify-space-between pa-4 pb-0">
      <v-card-title class="small-title">Project tasks & estimates</v-card-title>
      <div class="d-flex">
        <Button
          id="add-estimate-button"
          color="primary"
          @clickAction="addEstimateDialog = true"
        >
          {{ "Add" }}
        </Button>
        <v-menu
          transition="scale-transition"
          :offset-y="true"
        >
          <template #activator="{ on: menu }">
            <v-tooltip bottom>
              <template #activator="{ on: tooltip }">
                <Button
                  id="export-button"
                  class="pl-4"
                  color="primary"
                  :on="{ ...tooltip, ...menu }"
                >
                  {{ "Import from Excel" }}
                </Button>
              </template>
              <span>Excel options</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item @click="downloadTemplate">
              <v-list-item-title>Download template</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openFileDialog">
              <v-list-item-title>Import data</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <input
          ref="fileInput"
          type="file"
          accept=".xls,.xlsx"
          style="display: none"
          @change="onFileSelected"
        >
      </div>
    </div>
    <v-data-table
      class="assignments-table pb-4"
      item-key="id"
      disable-pagination
      hide-default-footer
      :headers="headers"
      :items="modifiedEstimations"
      :sort-by="['estimates.description']"
    >
      <template #item="{ item, headers, isSelected, select, index }">
        <ProjectEstimateRow
          :key="index"
          :assignment="item"
          :headers="headers"
          :selected="isSelected"
          :select="select"
          :estimation="item"
          :task-types="taskTypes"
          :role-items="roles"
          :allowed="allowed"
          @onDelete="askEstimateDeleteConfirmation"
          @onSave="saveEstimate"
        />
      </template>
    </v-data-table>

    <AddEstimateDialog
      :is-opened="addEstimateDialog"
      :task-type-items="taskTypes"
      :role-items="roles"
      :project="project"
      @close="addEstimateDialog = false"
    />

    <DeletionConfirmationDialog
      :dialog="deleteEstimateDialog"
      title="Confirmation"
      text="Are you sure you want to delete this estimate from the project?"
      confirmation-button-text="Delete"
      @confirmationAction="estimateDeleteConfirmation"
    />
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import AddEstimateDialog from '@/components/projects/AddEstimateDialog.vue';
import ProjectEstimateRow from '@/components/projects/ProjectEstimateRow.vue';
import {
  Button,
  Dialog as DeletionConfirmationDialog,
} from '@/components/shared';
import { DateHelper } from '@/helpers/date.helper';
import { FileSizeConverter } from '@/helpers/file-size-converter';
import apiClient from '@/services/api-client';

const MAX_FILES_SIZE = 10;

export default {
  components: {
    AddEstimateDialog,
    ProjectEstimateRow,
    Button,
    DeletionConfirmationDialog,
  },
  filters: {
    dateToDisplay: DateHelper.formatDateAccordingSettings,
  },
  props: {
    title: {
      type: String,
      required: false,
      default: 'Employees',
    },
    taskTypes: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    roles: {
      type: Array,
      required: true,
    },
    allowed: {
      type: Boolean,
      default: false,
    },
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      headers: [
        {
          text: 'Task',
          value: 'estimation.taskTypeTitle',
          sortable: false,
        },
        {
          text: 'Role',
          value: 'estimation.roleTypeTitle',
          sortable: false,
        },
        {
          text: 'Estimate',
          value: 'estimation.estimate',
          sortable: false,
        },
      ],
      addEstimateDialog: false,
      exportFromExcelDialog: false,
      deleteEstimateDialog: false,
      itemToDelete: '',
    };
  },
  computed: {
    ...mapGetters('projects/main', ['project', 'search']),
    ...mapGetters('auth/account', ['isUserAdmin']),
    modifiedEstimations() {
      const currentProjectTypes = this.taskTypes;
      const allTaskTypes = this.$store.getters['shared/taskTypes/allTaskTypes'];
      const fullTaskMap = Object.fromEntries(
        [...currentProjectTypes, ...allTaskTypes].map((t) => [t.id, t.title])
      );

      const roleMap = Object.fromEntries(
        this.roles.map((r) => [r.id, r.name])
      );

      return this.items.map((estimation) => {
        const taskTitleFromId = fullTaskMap[estimation.task_type];
        const isDirectString = typeof estimation.task_type === 'string' && !taskTitleFromId;

        return {
          ...estimation,
          taskTypeTitle: taskTitleFromId || (isDirectString
            ? estimation.task_type
            : `Unknown task (${estimation.task_type})`),
          roleTypeTitle: roleMap[estimation.role_id] || `Unknown role (${estimation.role_id})`,
        };
      });
    },
  },
  methods: {
    ...mapActions('projects/main', [
      'removeEstimateFromProject',
      'updateProjectEstimate',
      'getProjectEstimations',
      'importProjectEstimate',
      'getProjectEstimationRoles',
    ]),
    ...mapActions('shared/taskTypes', [
      'getAllTaskTypes',
      'getProjectsTaskTypes',
    ]),
    ...mapMutations('projects/main', ['']),

    openFileDialog() {
      this.$refs.fileInput.click();
    },
    async updateTaskTypes() {
      return Promise.all([
        this.getAllTaskTypes(),
        this.getProjectsTaskTypes({ projectIds: [this.project.id] }),
        this.getProjectEstimations({
          projectId: this.project.id,
        }),
      ]);
    },

    async downloadTemplate() {
      try {
        const response = await fetch('/templates/estimates_template.xlsx');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = 'estimates_template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.flashError('Failed to download template');
      }
    },

    async onFileSelected(event) {
      const file = event.target.files[0];

      if (!file) return;

      const name = file.name.toLowerCase();

      if (!name.endsWith('.xlsx')) {
        this.flashError('Only .xlsx files are allowed');
        event.target.value = null;

        return;
      }

      const allowedMime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      if (file.type !== allowedMime) {
        this.flashError('Only XLSX files are allowed');
        event.target.value = null;

        return;
      }

      const fileSizeMB = Number(FileSizeConverter.converToMB(file.size).slice(0, -2));

      if (fileSizeMB >= MAX_FILES_SIZE) {
        this.flashError(`Files with a total size of ${MAX_FILES_SIZE}MB can't be uploaded.`);
        event.target.value = null;

        return;
      }

      const formData = new FormData();

      formData.append('file', file, file.name);

      try {
        await this.importProjectEstimate({
          projectId: this.project.id,
          file: formData,
        });
        this.$emit('afterImport');
        await this.getProjectEstimationRoles({
          projectId: this.project.id,
        });
        await this.getProjectEstimations({
          projectId: this.project.id,
        });
      } catch (err) {
        console.error(err);
        this.$emit('flashError', 'Failed to import estimates');
      } finally {
        event.target.value = null;
      }
    },

    askEstimateDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.deleteEstimateDialog = true;
    },
    async estimateDeleteConfirmation(confirmed) {
      if (confirmed) {
        await this.removeEstimateFromProject({
          estimateId: this.itemToDelete.id,
        });
        await this.getProjectEstimations({
          projectId: this.project.id,
        });
      }

      this.deleteEstimateDialog = false;
    },
    async saveEstimate(estimate) {
      try {
        await this.updateProjectEstimate({
          estimateId: estimate.id,
          estimate,
        });

        if (estimate.taskTypeTitle && !this.taskTypes.includes(estimate.taskTypeTitle)) {
          await apiClient.taskTypeApi.updateTaskTypeById(estimate.task_type, {
            title: estimate.taskTypeTitle,
            owner_project_id: this.project.id,
          });
        }
      } catch (error) {
        this.flashError('An error occured while updating estimate.');
      } finally {
        await this.updateTaskTypes();
      }
    },
  },
};
</script>

<style lang="less">
.assignments-table {
  overflow: auto;

  .ripple-scale.v-input--selection-controls:not(.v-input--switch)
    .v-input--selection-controls__ripple {
    transform: scale(0.6);
  }

  table .shadow {
    color: darkgray;
  }
}

.action-column {
  .row {
    margin: 0;

    .v-btn {
      color: #555;
      margin: 1px;
      height: inherit;
      width: inherit;

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
        transition: all 0.5s ease;
      }
    }
  }
}
</style>
